import { useCallback, useEffect, useMemo, useState } from 'react'

import * as R from 'ramda'
import { useSelector } from 'react-redux'

import { fetchCutover } from 'api/customer'
import * as OrgSelectors from 'modules/org/selectors'

const fetchAndSet = <T extends object>(
  promise: Promise<T>,
  setCutover: (value: T) => void
) =>
  promise.then(setCutover).catch((error) => {
    console.error('Fetch error:', error)
  })

/***
 * Hook that interfaces with api to cutover by namespace - namespace must align with value in customer-api
 *
 * @returns {object} { hasCutover: boolean, loading: boolean }
 */
export const useCutover = (namespace: string) => {
  const orgId = useSelector(OrgSelectors.orgId)

  const [loading, setLoading] = useState(false)
  const [hasFetchedCutover, setHasFetchedCutover] = useState(false)
  const [hasCutover, setHasCutover] = useState(false)

  const getCutover = useCallback(async () => {
    setLoading(true)
    await fetchAndSet(fetchCutover(orgId, namespace), (data) =>
      setHasCutover(R.pathOr(false, [namespace], data))
    )
    setLoading(false)
    setHasFetchedCutover(true)
  }, [namespace, orgId])

  useEffect(() => {
    getCutover()
  }, [getCutover])

  return useMemo(
    () => ({
      hasCutover,
      hasFetchedCutover,
      loading,
    }),
    [hasCutover, loading, hasFetchedCutover]
  )
}

export default useCutover
